/**
 * Core Model Domain
 *
 * Unified source of truth for all model-specific configuration and the
 * route-visibility rules derived from it. Kept separate from constants.ts
 * (flat hardcoded values) because this is domain logic, not a value.
 */

// CoreModel Info - Unified source of truth for all model-specific configuration
export const CORE_MODEL_INFO = {
  Lite: {
    short: 'Lite',
    full: 'Core Lite',
    imageSrc: '/assets/images/installation-guide/core-lite-cover.png',
    description: 'Compact model for smaller deployments',
    serialPattern: /^F1[67][0123456789ACDEFHKMNPRTVWXY]{6}$/i,
    placementImage: '/assets/images/installation-guide/core-placement-lite.png',
    placementFlowId: 'core-lite-placement',
    // Lite is v1-only for now; OSv2 doesn't have a Lite model yet (Lite AI is planned ~mid-2027).
    availableOn: ['v1'] as const,
    restrictedFromManualSelection: false,
  },
  Pro: {
    short: 'Pro',
    full: 'Core Pro',
    imageSrc: '/assets/images/installation-guide/core-pro-cover.png',
    description: 'Standard professional model',
    serialPattern: /^F1[1289][0123456789ACDEFHKMNPRTVWXY]{6}$/i,
    placementImage: '/assets/images/installation-guide/core-placement-pro.png',
    placementFlowId: 'core-pro-placement',
    availableOn: ['v1', 'v2'] as const,
    restrictedFromManualSelection: false,
  },
  Pro_AI: {
    short: 'Pro AI',
    full: 'Core Pro AI',
    imageSrc: '/assets/images/installation-guide/core-pro-cover.png',
    description: 'Professional model with AI capabilities',
    serialPattern: /^F15[0123456789ACDEFHKMNPRTVWXY]{6}$/i,
    placementImage: '/assets/images/installation-guide/core-placement-pro.png',
    placementFlowId: 'core-pro-placement', // Use Pro flow until dedicated flow is authored
    availableOn: ['v1', 'v2'] as const,
    restrictedFromManualSelection: false,
  },
  Elite: {
    short: 'Elite',
    full: 'Core Elite',
    // No dedicated non-AI Elite images exist yet - reusing Elite AI's, the closest model
    // (same hardware family, minus the AI accelerator).
    imageSrc: '/assets/images/installation-guide/core-elite-cover.png',
    description: 'Premium model without AI features',
    // No confirmed serial mask for non-AI Elite from any source yet - which (if any) of the
    // legacy Elite SKUs are this hardware is still an open product/hardware question.
    // Intentionally non-matching placeholder so this can't silently misidentify a real serial;
    // harmless today since v2 client-side matching already excludes this model via availableOn.
    serialPattern: /^NO-CONFIRMED-ELITE-SERIAL-MASK$/,
    placementImage: '/assets/images/installation-guide/core-placement-elite.png',
    placementFlowId: 'core-elite-placement',
    // v1-only, matching product's stated device list (OSv2 only has "Core Elite AI", not plain
    // Elite) - same precedent as Lite.
    availableOn: ['v1'] as const,
    restrictedFromManualSelection: false,
  },
  Elite_AI: {
    short: 'Elite AI',
    full: 'Core Elite AI',
    imageSrc: '/assets/images/installation-guide/core-elite-cover.png',
    description: 'Premium model with advanced AI features',
    serialPattern: /^F(13|20)[0123456789ACDEFHKMNPRTVWXY]{6}$/i,
    placementImage: '/assets/images/installation-guide/core-placement-elite.png',
    placementFlowId: 'core-elite-placement', // Use Elite flow (not elite_ai)
    availableOn: ['v1', 'v2'] as const,
    restrictedFromManualSelection: false,
  },
  Mobile_AI: {
    short: 'Mobile AI',
    full: 'Core Mobile AI',
    imageSrc: '/assets/images/installation-guide/core-pro-cover.png',
    description: 'Mobile deployment model with AI capabilities',
    serialPattern: /^F(14|21)[0123456789ACDEFHKMNPRTVWXY]{6}$/i,
    placementImage: '/assets/images/installation-guide/core-placement-pro.png',
    placementFlowId: 'core-pro-placement', // Use Pro flow until dedicated flow is authored
    availableOn: ['v1', 'v2'] as const,
    // Limited availability (e.g. Serco) - only ever set via serial/SKU auto-detection,
    // never offered as a manual pick in the generic "select your Core" list on either route.
    restrictedFromManualSelection: true,
  },
} as const;

/** The two setup site experiences a model can be offered on. */
export type OSRoute = 'v1' | 'v2';

/** Union of all supported Core model identifiers, derived from CORE_MODEL_INFO. */
export type CoreModel = keyof typeof CORE_MODEL_INFO;

/** Whether a model is offered at all (auto-detection, guide content, etc.) on the given route. */
export const isModelAvailableOnRoute = (model: CoreModel, route: OSRoute): boolean =>
  (CORE_MODEL_INFO[model].availableOn as readonly OSRoute[]).includes(route);

/**
 * Whether a model should be offered as a manual pick in the generic
 * "select your Core" list on the given route - i.e. available on that route
 * AND not restricted to auto-detection only (e.g. limited-availability models).
 */
export const isManuallySelectableOnRoute = (model: CoreModel, route: OSRoute): boolean =>
  isModelAvailableOnRoute(model, route) && !CORE_MODEL_INFO[model].restrictedFromManualSelection;
